import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { lensLabel } from "../lib/lenses.js";
import { useShellEvents } from "../lib/useShellEvents.js";

const DAY_MS = 864e5;
const RING = 2 * Math.PI * 8;

function ago(ms) {
  const h = ms / 36e5;
  if (h < 1) return "just now";
  if (h < 24) return `${Math.round(h)} h ago`;
  return `${Math.round(h / 24)} d ago`;
}

// How much of the hold is left, as a ring that empties over HOLD_DAYS.
function Ring({ left }) {
  return (
    <svg className={`app-ring${left < 0.2 ? " low" : ""}`} viewBox="0 0 20 20" aria-hidden="true">
      <circle className="t" cx="10" cy="10" r="8" />
      <circle className="f" cx="10" cy="10" r="8" strokeDasharray={RING.toFixed(2)} strokeDashoffset={(RING * (1 - left)).toFixed(2)} />
    </svg>
  );
}

/**
 * Working memory: the button in the top bar (count and one pip per slot) and the panel it opens.
 * Held items follow the user across lenses and fade after HOLD_DAYS unless reopened.
 * The top bar owns `open`, so opening search can close this panel.
 */
export default function MemoryPanel({ shell, lens, lenses, open, setOpen }) {
  const boxRef = useRef(null);
  const btnRef = useRef(null);
  useShellEvents(shell, ["held"], 60000);

  // Close on a press outside, or on Escape before the open lens sees the key.
  useEffect(() => {
    if (!open) return undefined;
    const onDown = e => {
      if (!boxRef.current?.contains(e.target)) setOpen(false);
    };
    const onKey = e => {
      if (e.key !== "Escape") return;
      e.stopPropagation();
      setOpen(false);
      btnRef.current?.focus();
    };
    document.addEventListener("pointerdown", onDown);
    window.addEventListener("keydown", onKey, true);
    return () => {
      document.removeEventListener("pointerdown", onDown);
      window.removeEventListener("keydown", onKey, true);
    };
  }, [open, setOpen]);

  const { CAP, HOLD_DAYS, day: D } = shell;
  const held = shell.held();
  const library = shell.library();
  const team = shell.team();
  const close = () => setOpen(false);
  // Which library shelf the panel lists: this person's own, or the project's team shelf (DEC-042).
  const [chosen, setShelf] = useState("mine");
  const shelf = shell.canShare ? chosen : "mine";
  const kept = shelf === "team" ? team : library;

  // Items from earlier days, and arrivals beyond the shortlist, are on the stage only: today's
  // chart, brain and stream don't have them.
  const offList = p => p.earlier || p.arrival;
  const linksFor = p => lenses.filter(l => l.id !== lens && !(offList(p) && l.id !== "today"));

  const consolidate = to => {
    const n = shell.consolidate(to);
    shell.toast(`Moved ${n} item${n === 1 ? "" : "s"} to ${to === "team" ? "the team shelf" : "your library"}.`);
    setShelf(to);
  };

  return (
    <div className="app-wm-box" ref={boxRef}>
      <button
        ref={btnRef}
        type="button"
        className="app-btn app-wm"
        aria-expanded={open}
        aria-controls="app-wm-panel"
        aria-label={`Working memory, ${held.length} of ${CAP} held`}
        onClick={() => setOpen(!open)}
      >
        <span className="app-wm-label">Memory</span>
        <span>
          <b>{held.length}</b>/{CAP}
        </span>
        <span className="app-pips" aria-hidden="true">
          {Array.from({ length: CAP }, (_, i) => (
            <i key={i} className={held[i] ? "on" : ""} style={held[i] ? { opacity: 0.3 + 0.7 * shell.fade(held[i]) } : undefined} />
          ))}
        </span>
      </button>

      {open && (
        <section className="app-wm-panel" id="app-wm-panel" aria-label="Working memory">
          <div className="app-wm-head">
            <h2>Working memory</h2>
            <span>
              {held.length} of {CAP}
            </span>
          </div>
          <p className="app-wm-note">
            Held items follow you across every lens and fade after {HOLD_DAYS} days unless you open them again. When all {CAP} slots
            are full, the oldest moves to your library.
          </p>
          {held.length === 0 && <p className="app-wm-empty">Nothing held. Use Hold on any item, or press A in Focus.</p>}
          <ul className="app-wm-list">
            {held
              .slice()
              .reverse()
              .map(h => {
                const p = D.paperById[h.id];
                const a = p && D.areaById[p.area];
                if (!p || !a) return null;
                const daysLeft = Math.max(0, HOLD_DAYS - (Date.now() - h.at) / DAY_MS);
                const openIn = offList(p) && lens !== "today" ? "today" : lens;
                return (
                  <li key={h.id} className="app-wm-item">
                    <Ring left={shell.fade(h)} />
                    <div>
                      <Link
                        className="app-wm-title"
                        to={shell.link(openIn, p.id)}
                        onClick={() => {
                          shell.rehearse(p.id);
                          close();
                        }}
                      >
                        {p.title}
                      </Link>
                      <span className="app-wm-meta">
                        {a.short || a.name}
                        {p.earlier && p.day ? ` · from ${p.day}` : ""} · held {ago(Date.now() - h.at)} in {lensLabel(h.from)} ·{" "}
                        {daysLeft < 1 ? "fades today" : `fades in ${Math.ceil(daysLeft)} d`}
                      </span>
                      <span className="app-go">
                        {linksFor(p).map(l => (
                          <Link key={l.id} to={shell.link(l.id, p.id)} title={`Open in ${l.label}`} onClick={close}>
                            {l.short}
                          </Link>
                        ))}
                        {p.url && (
                          <a href={p.url} target="_blank" rel="noopener noreferrer" title="Open the item at its source">
                            Source ↗
                          </a>
                        )}
                      </span>
                    </div>
                    <button type="button" className="app-x" aria-label={`Release ${p.title}`} onClick={() => shell.release(p.id)}>
                      ×
                    </button>
                  </li>
                );
              })}
          </ul>
          <div className="app-wm-foot">
            {shell.canShare ? (
              <>
                <span>Move all held to</span>
                <span className="app-cons-pair">
                  <button type="button" className="app-cons" disabled={!held.length} onClick={() => consolidate("mine")}>
                    My library
                  </button>
                  <button type="button" className="app-cons alt" disabled={!held.length} onClick={() => consolidate("team")}>
                    Team shelf
                  </button>
                </span>
              </>
            ) : (
              <>
                <span>
                  Library · {library.length} item{library.length === 1 ? "" : "s"}
                </span>
                <button type="button" className="app-cons" disabled={!held.length} onClick={() => consolidate("mine")}>
                  Consolidate to library
                </button>
              </>
            )}
          </div>

          <div className="app-lib">
            <div className="app-lib-tabs" role="tablist" aria-label="Library">
              <button type="button" role="tab" aria-selected={shelf === "mine"} onClick={() => setShelf("mine")}>
                My library · {library.length}
              </button>
              {shell.canShare && (
                <button type="button" role="tab" aria-selected={shelf === "team"} onClick={() => setShelf("team")}>
                  Team shelf · {team.length}
                </button>
              )}
            </div>
            {shelf === "team" && <p className="app-wm-note">Kept for everyone in this project. It stays when people leave.</p>}
            {kept.length === 0 && (
              <p className="app-wm-empty">
                {shelf === "team"
                  ? "Nothing on the team shelf yet. Share a paper from your library, or move what you hold here."
                  : "Nothing kept yet. Move what you hold here; when memory is full, the oldest comes here by itself."}
              </p>
            )}
            <ul className="app-wm-list app-lib-list">
              {kept.map(k => {
                const p = D.paperById[k.id];
                const a = p && D.areaById[p.area];
                if (!p || !a) return null;
                const openIn = offList(p) && lens !== "today" ? "today" : lens;
                const when = ago(Date.now() - k.at);
                return (
                  <li key={k.id} className="app-wm-item app-lib-item">
                    <div>
                      <Link className="app-wm-title" to={shell.link(openIn, p.id)} onClick={close}>
                        {p.title}
                      </Link>
                      <span className="app-wm-meta">
                        {a.short || a.name}
                        {p.earlier && p.day ? ` · from ${p.day}` : ""} ·{" "}
                        {shelf === "team" ? `added by ${k.by || "a former member"} ${when}` : `kept ${when}`}
                      </span>
                      <span className="app-go">
                        {p.url && (
                          <a href={p.url} target="_blank" rel="noopener noreferrer" title="Open the item at its source">
                            Source ↗
                          </a>
                        )}
                        {shelf === "mine" &&
                          shell.canShare &&
                          (shell.isShared(k.id) ? (
                            <span className="app-lib-on">On the team shelf</span>
                          ) : (
                            <button type="button" className="app-lib-share" onClick={() => shell.shareWithTeam(k.id)}>
                              Share with team
                            </button>
                          ))}
                        {shelf === "team" &&
                          (shell.isKept(k.id) ? (
                            <span className="app-lib-on">In my library</span>
                          ) : (
                            <button
                              type="button"
                              className="app-lib-share"
                              title="Keep a copy in your own library; it stays if the team takes it off the shelf"
                              onClick={() => shell.keepForMe(k.id)}
                            >
                              Keep for me
                            </button>
                          ))}
                      </span>
                    </div>
                    {(shelf === "mine" || k.canRemove) && (
                      <button
                        type="button"
                        className="app-x"
                        aria-label={shelf === "mine" ? `Remove ${p.title} from your library` : `Take ${p.title} off the team shelf`}
                        onClick={() => (shelf === "mine" ? shell.forget(k.id) : shell.unshare(k.id))}
                      >
                        ×
                      </button>
                    )}
                  </li>
                );
              })}
            </ul>
          </div>
        </section>
      )}
    </div>
  );
}
